import React, { useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
    Box,
    Stack,
    Typography,
    Button,
    CircularProgress,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';

import { authTokenAtom } from 'atoms/authAtoms';
import { powertrainAtom } from 'atoms/powertrainAtom';
import { recentCalcsAtom } from 'atoms/recentCalcsAtom';
import { Config } from 'atoms/Rtool/newConfigAtoms';
import {
    referenceConfigAtom,
    projectIdAtom,
    destinationIdAtom,
    lotIdAtom,
    phaseIdAtom,
    unitIdAtom,
    calculationIdAtom,
} from 'atoms/Rtool/referenceConfigAtoms';
import { ProjectSelect } from 'utils/projectSelector/ProjectSelect';
import { DestinationSelect } from 'utils/projectSelector/DestinationSelect';
import { LotSelect } from 'utils/projectSelector/LotSelect';
import { PhaseSelect } from 'utils/projectSelector/PhaseSelect';
import { UnitSelect } from 'utils/projectSelector/UnitSelect';
import { CalculationSelect } from 'utils/projectSelector/CalculationSelect';
import { useCalculationSet } from 'utils/hooks/useRToolTree';
import type { CalculationSetData } from 'api/fetchRtool';

type Props = {
    open: boolean;
    onClose: () => void;
    onApply: (config: Config, data: CalculationSetData) => void;
};

const EMPTY_REFERENCE: Config = {
    project_id: '',
    destination_id: '',
    lot_id: '',
    phase_id: '',
    unit_id: '',
    calculation_id: '',
    condition_id: '',
    graph_id: '',
};

// 新規計算セット作成フォームから開く「既存計算セットから複製」ダイアログ（R）。
// 参照元は (1) 最近使った計算セット、(2) 階層カスケードの 2 経路で指定でき、どちらも
// 同じ referenceConfigAtom に書き込むため併用可能。確定後の payload は useCalculationSet で取得する。
export const ReferenceCopyDialog: React.FC<Props> = ({ open, onClose, onApply }) => {
    const authToken = useAtomValue(authTokenAtom);
    const powertrain = useAtomValue(powertrainAtom);
    const recents = useAtomValue(recentCalcsAtom);
    const refConfig = useAtomValue(referenceConfigAtom);
    const setRefConfig = useSetAtom(referenceConfigAtom);
    const { data, loading } = useCalculationSet(authToken, refConfig);

    // 開くたびに参照元をクリアし、毎回まっさらな状態から選べるようにする。
    useEffect(() => {
        if (open) setRefConfig(EMPTY_REFERENCE);
    }, [open, setRefConfig]);

    // 現在の powertrain・R ツールの履歴のみを候補にする（cross-powertrain は扱わない）。
    const rRecents = recents.filter((r) => r.tool === 'r' && r.powertrain === powertrain);

    // 現在の参照元が履歴のどれかに一致すればその index を value にする（カスケードを手で
    // 変えて履歴と一致しなくなったら自動で「選択しない」に戻す）。
    const recentIndex = rRecents.findIndex(
        (r) =>
            r.config.project_id === refConfig.project_id &&
            r.config.destination_id === refConfig.destination_id &&
            r.config.lot_id === refConfig.lot_id &&
            r.config.phase_id === refConfig.phase_id &&
            (r.config.unit_id ?? '') === refConfig.unit_id &&
            r.config.calculation_id === refConfig.calculation_id,
    );
    const recentValue = recentIndex >= 0 ? String(recentIndex) : '';

    const handleRecentChange = (value: string) => {
        if (value === '') return;
        const r = rRecents[Number(value)];
        if (!r) return;
        // 子段クリアの連鎖を避けるため root を一括で set する。
        setRefConfig({
            project_id: r.config.project_id,
            destination_id: r.config.destination_id,
            lot_id: r.config.lot_id,
            phase_id: r.config.phase_id,
            unit_id: r.config.unit_id ?? '',
            calculation_id: r.config.calculation_id,
            condition_id: '',
            graph_id: '',
        });
    };

    const refComplete = !!(
        refConfig.project_id &&
        refConfig.destination_id &&
        refConfig.lot_id &&
        refConfig.phase_id &&
        refConfig.unit_id &&
        refConfig.calculation_id
    );

    const handleApply = () => {
        if (!data) return;
        onApply(refConfig, data);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>既存計算セットから複製</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    既存計算セットを選ぶと、その内容をフォームに仮入力します。作成時は参照元と
                    完全一致だと弾かれるため、いずれかの項目を変更してください。
                </Typography>

                <FormControl fullWidth sx={{ mb: 1 }} disabled={rRecents.length === 0}>
                    <InputLabel id="ref-recent-select-label">最近使った計算セット</InputLabel>
                    <Select
                        labelId="ref-recent-select-label"
                        value={recentValue}
                        label="最近使った計算セット"
                        onChange={(e) => handleRecentChange(e.target.value as string)}
                    >
                        <MenuItem value="">
                            <em>選択しない</em>
                        </MenuItem>
                        {rRecents.map((r, i) => (
                            <MenuItem key={`${r.label}-${i}`} value={String(i)}>
                                {r.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Divider sx={{ my: 2 }}>または階層から選択</Divider>

                <Stack spacing={2} direction="column" alignItems="stretch">
                    <ProjectSelect atom={projectIdAtom} />
                    <DestinationSelect atom={destinationIdAtom} config={refConfig} />
                    <LotSelect atom={lotIdAtom} config={refConfig} />
                    <PhaseSelect atom={phaseIdAtom} config={refConfig} />
                    <UnitSelect atom={unitIdAtom} config={refConfig} />
                    <CalculationSelect atom={calculationIdAtom} config={refConfig} />
                </Stack>

                <Box sx={{ mt: 2, minHeight: 24 }}>
                    {refComplete && loading && <CircularProgress size={20} />}
                    {refComplete && !loading && data && (
                        <Typography variant="body2" color="text.secondary">
                            反映内容：Mount {data.mtg} / {data.description || '（説明なし）'}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>キャンセル</Button>
                <Button
                    variant="contained"
                    onClick={handleApply}
                    disabled={!refComplete || loading || !data}
                >
                    反映
                </Button>
            </DialogActions>
        </Dialog>
    );
};
